import { Component, Input, OnInit } from '@angular/core';
import { PocketbaseService } from '../../services/pocketbase.service';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { NgClass } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { firstValueFrom, timer } from 'rxjs';

type PurchaseType = 'course' | 'book' | 'bundle';
type PaymentMethod = 'paypal' | 'creditcard';

@Component({
    selector: 'app-purchase-page',
    imports: [NgClass, ReactiveFormsModule, RouterLink],
    templateUrl: './purchase-page.component.html',
    styleUrl: './purchase-page.component.scss'
})
export class PurchasePageComponent implements OnInit {
  @Input()
  courseId!: string;

  purchaseDescription?: PurchaseDescription;
  purchaseType: PurchaseType = 'course';
  paymentMethod: PaymentMethod = 'paypal';
  billingAdressForm!: FormGroup;

  pending = false;
  success?: boolean;
  bookDownloadUrl?: string;
  errorMessage?: string;

  constructor(
    private pocketbaseService: PocketbaseService,
    private route: ActivatedRoute,
    private fb: FormBuilder
  ) {
    this.billingAdressForm = this.fb.group({
      title: ['', [Validators.required]],
      givenName: ['', [Validators.required, Validators.minLength(2)]],
      familyName: ['', [Validators.required, Validators.minLength(2)]],
      organizationName: [''],
      streetAndNumber: ['', [Validators.required]],
      streetAdditional: [''],
      postalCode: ['', [Validators.required]],
      city: ['', [Validators.required]],
      region: [''],
      country: [{ value: 'Deutschland', disabled: true }],
      saveDetails: [false],
      acceptTerms: [false, [Validators.requiredTrue]]
    });
  }

  async ngOnInit() {
    this.courseId = this.resolveCourseId();
    if (!this.courseId) {
      this.success = false;
      this.errorMessage = 'Kurs konnte nicht geladen werden.';
      return;
    }

    const requestedPurchaseType = this.route.snapshot.queryParamMap.get('purchaseType');
    if (requestedPurchaseType === 'book' || requestedPurchaseType === 'bundle') {
      this.purchaseType = requestedPurchaseType;
    }
    const requestedPaymentMethod = this.route.snapshot.queryParamMap.get('paymentMethod');
    if (requestedPaymentMethod === 'paypal' || requestedPaymentMethod === 'creditcard') {
      this.paymentMethod = requestedPaymentMethod;
    }

    try {
      this.purchaseDescription = await this.pocketbaseService.pb.send<PurchaseDescription>(`/purchase/${this.courseId}?purchaseType=${this.purchaseType}`, { method: 'GET' });
      await this.loadBookDownloadUrl();
    } catch (error) {
      this.success = false;
      this.errorMessage = this.extractErrorMessage(error);
      return;
    }

    const canceled = this.route.snapshot.queryParamMap.get('canceled');
    if (canceled && canceled.toLowerCase() === 'true') {
      this.success = false;
      this.errorMessage = this.getCheckoutStatusMessage('canceled');
      return;
    }

    const post_checkout = this.route.snapshot.queryParamMap.get('post_checkout');
    if (post_checkout && post_checkout.toLowerCase() === 'true') {
      this.pending = true;
      this.errorMessage = undefined;

      // Mollie webhook callbacks can be delayed; poll for a bit longer before showing an error.
      const maxAttempts = 30;
      const pollIntervalMs = 1000;

      for (let i = 0; i < maxAttempts; i++) {
        if (!this.pending) break;

        try {
          const confirmation = await this.pocketbaseService.pb.send<{ status?: string, mollieStatus?: string }>('/purchase/confirm', {
            method: 'POST',
            body: {
              courseId: this.courseId,
              purchaseType: this.purchaseType
            }
          });

          const status = (confirmation?.status || '').toLowerCase();
          const mollieStatus = (confirmation?.mollieStatus || '').toLowerCase();

          if (status === 'paid' || mollieStatus === 'paid') {
            this.pending = false;
            this.success = true;
          } else if (
            status === 'failed' ||
            mollieStatus === 'failed' ||
            mollieStatus === 'canceled' ||
            mollieStatus === 'expired'
          ) {
            this.pending = false;
            this.success = false;
            this.errorMessage = this.getCheckoutStatusMessage(mollieStatus || status);
          }
        } catch (ignored) {
          // expected to throw an error if the purchase is not yet paid, ignoring
        }

        await firstValueFrom(timer(pollIntervalMs));
      }

      if (this.pending) {
        this.pending = false;
        this.success = false;
        this.errorMessage = 'Die Zahlung wird noch verarbeitet. Bitte warte einen Moment und lade die Seite erneut.';
      }
    }
  }

  onSubmit() {
    if (this.pending) {
      return;
    }

    if (this.billingAdressForm.invalid) {
      Object.keys(this.billingAdressForm.controls).forEach(key => {
        const control = this.billingAdressForm.get(key);
        if (control) {
          control.markAsTouched();
        }
      });
      return;
    }

    this.errorMessage = undefined;

    // Continue with form submission
    const billingAddress: BillingAddress = {
      title: this.billingAdressForm.get('title')?.value,
      givenName: this.billingAdressForm.get('givenName')?.value,
      familyName: this.billingAdressForm.get('familyName')?.value,
      organizationName: this.billingAdressForm.get('organizationName')?.value,
      streetAndNumber: this.billingAdressForm.get('streetAndNumber')?.value,
      streetAdditional: this.billingAdressForm.get('streetAdditional')?.value,
      postalCode: this.billingAdressForm.get('postalCode')?.value,
      city: this.billingAdressForm.get('city')?.value,
      region: this.billingAdressForm.get('region')?.value,
      country: 'DE'
    }
    this.initiatePurchase(billingAddress);
  }

  async initiatePurchase(address: BillingAddress) {
    if (this.pending) {
      return;
    }
    this.pending = true;

    const courseId = this.resolveCourseId();
    if (!courseId) {
      this.pending = false;
      this.success = false;
      this.errorMessage = 'Kurs konnte nicht geladen werden.';
      return;
    }

    const purchaseQuery = `purchaseType=${this.purchaseType}&paymentMethod=${this.paymentMethod}`;

    try {
      const response = await this.pocketbaseService.pb.send<{ checkoutUrl: string }>('/purchase', {
        method: 'POST', body: {
          courseId,
          purchaseType: this.purchaseType,
          paymentMethod: this.paymentMethod,
          redirectUrl: `${location.protocol}//${location.host}${location.pathname}?${purchaseQuery}&post_checkout=true`,
          cancelUrl: `${location.protocol}//${location.host}${location.pathname}?${purchaseQuery}&canceled=true`,
          billingAddress: address
        }
      });
      window.location.href = response.checkoutUrl;
    } catch (error) {
      this.pending = false;
      this.success = false;
      this.errorMessage = this.extractErrorMessage(error);
    }
  }

  private async loadBookDownloadUrl() {
    if (this.purchaseType !== 'book' && this.purchaseType !== 'bundle') {
      this.bookDownloadUrl = undefined;
      return;
    }

    const courseId = this.resolveCourseId();
    if (!courseId) {
      this.bookDownloadUrl = undefined;
      return;
    }

    const course = await this.pocketbaseService.pb.collection('courses').getOne<{ id: string, bookPdf?: string | string[] }>(courseId, {
      fields: 'id,bookPdf'
    });

    const fileName = this.extractFileName(course.bookPdf);
    this.bookDownloadUrl = fileName
      ? await this.pocketbaseService.getCourseFileUrl(course.id, fileName, true)
      : undefined;
  }

  private extractFileName(file?: string | string[]): string | undefined {
    if (Array.isArray(file)) {
      return file[0];
    }

    return file;
  }

  formatAmount(value?: string): string {
    const numericValue = Number(value ?? 0);

    if (Number.isNaN(numericValue)) {
      return value ?? '0,00';
    }

    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numericValue);
  }

  formatPercent(value?: string): string {
    const numericValue = Number(value ?? 0);

    if (Number.isNaN(numericValue)) {
      return value ?? '0';
    }

    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(numericValue);
  }

  private resolveCourseId(): string {
    return this.courseId || this.route.snapshot.paramMap.get('courseId') || '';
  }

  private extractErrorMessage(error: unknown): string {
    const apiMessage = (error as { response?: { message?: string } })?.response?.message;
    if (apiMessage && apiMessage.trim().length > 0) {
      return apiMessage;
    }

    const defaultMessage = (error as { message?: string })?.message;
    if (defaultMessage && defaultMessage.trim().length > 0) {
      return defaultMessage;
    }

    return 'Bitte versuche es später erneut.';
  }

  private getCheckoutStatusMessage(status?: string): string {
    const normalizedStatus = (status || '').toLowerCase();

    if (normalizedStatus === 'failed') {
      return 'Deine Zahlung ist fehlgeschlagen. Bitte prüfe dein Zahlungsmittel und versuche es erneut.';
    }

    if (normalizedStatus === 'canceled') {
      return 'Deine Zahlung wurde abgebrochen.';
    }

    if (normalizedStatus === 'expired') {
      return 'Die Zahlungssitzung ist abgelaufen. Bitte starte den Kauf erneut.';
    }

    return 'Es ist ein Fehler bei der Zahlungsbestätigung aufgetreten. Bitte versuche es erneut.';
  }

  public async downloadBook(): Promise<void> {
    const courseId = this.resolveCourseId();
    if (!courseId || (this.purchaseType !== 'book' && this.purchaseType !== 'bundle')) {
      return;
    }

    try {
      const course = await this.pocketbaseService.pb.collection('courses').getOne<{ id: string, bookPdf?: string | string[] }>(courseId, {
        fields: 'id,bookPdf'
      });

      const fileName = this.extractFileName(course.bookPdf);
      if (!fileName) return;

      // FRESH TOKEN at download time (not cached from initialization)
      const downloadUrl = await this.pocketbaseService.getCourseFileUrl(course.id, fileName, true);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download book:', error);
      alert('Der Download konnte leider nicht gestartet werden. Bitte versuche es später erneut.');
    }
  }

  retryPurchase(): void {
    const courseId = this.resolveCourseId();
    if (!courseId) {
      return;
    }

    // Force a full reload so the component initializes from a clean state.
    window.location.href = `/purchase/${courseId}?purchaseType=${this.purchaseType}&paymentMethod=${this.paymentMethod}`;
  }

  setPaymentMethod(method: PaymentMethod): void {
    this.paymentMethod = method;
  }

  private isUnknownFieldError(error: unknown, fieldName: string): boolean {
    const details = (error as { response?: { data?: { details?: string } } })?.response?.data?.details || '';
    return details.includes(`unknown field "${fieldName}"`);
  }
}

interface Amount {
  currency: string;
  value: string;
}

interface Line {
  type: string;
  description: string;
  quantity: number;
  unitPrice: Amount;
  totalAmount: Amount;
  vatRate: string;
  vatAmount: Amount;
}

interface PurchaseDescription {
  description: string;
  amount: Amount;
  lines: Line[];
}

interface BillingAddress {
  title: string;
  givenName: string;
  familyName: string;
  organizationName?: string;
  streetAndNumber: string;
  streetAdditional?: string;
  postalCode: string;
  city: string;
  region?: string;
  country: string;
}
